import React from 'react';
import { styled } from 'baseui';
import { S3Image } from './S3Image';

export interface UserAvatarProps {
  photoUrl?: string | null;
  name?: string;
  lastName?: string;
  size?: number;
  shape?: 'circle' | 'square';
  showInitials?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const AvatarContainer = styled('div', {
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#f0f0f0',
  border: '2px solid #e0e0e0',
  flexShrink: 0,
});

const InitialsContainer = styled('div', {
  fontWeight: '600',
  color: '#666',
  textTransform: 'uppercase',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
});

/**
 * UserAvatar component for displaying user profile pictures
 * @param photoUrl - The S3 key or existing URL for the user photo
 * @param name - User's first name
 * @param lastName - User's last name
 * @param size - Size of the avatar in pixels (default: 40)
 * @param shape - Shape of the avatar: 'circle' or 'square' (default: 'circle')
 * @param showInitials - Whether to show initials as fallback (default: true)
 * @param className - Additional CSS class
 * @param style - Additional CSS styles
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  photoUrl,
  name = '',
  lastName = '',
  size = 40,
  shape = 'circle',
  showInitials = true,
  className,
  style,
}) => {
  // Generate user initials
  const getInitials = (): string => {
    const firstInitial = name?.charAt(0) || '';
    const lastInitial = lastName?.charAt(0) || '';
    const initials = `${firstInitial}${lastInitial}`.toUpperCase();
    
    if (initials.length === 0) {
      return '👤';
    }
    
    return initials;
  };

  const fallbackComponent = showInitials ? (
    <InitialsContainer 
      style={{ 
        fontSize: `${Math.max(12, size * 0.4)}px`,
        color: style?.backgroundColor === '#000000' ? '#ffffff' : '#666'
      }}
    >
      {getInitials()}
    </InitialsContainer>
  ) : (
    <InitialsContainer 
      style={{ 
        fontSize: `${Math.max(12, size * 0.4)}px`,
        color: style?.backgroundColor === '#000000' ? '#ffffff' : '#666'
      }}
    >
      👤
    </InitialsContainer>
  );

  return (
    <AvatarContainer 
      className={className}
      style={{
        ...style,
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: shape === 'circle' ? '50%' : '8px',
      }}
    >
      <S3Image
        imageKey={photoUrl}
        alt={`${name} ${lastName}`.trim() || 'User avatar'}
        fallbackComponent={fallbackComponent}
        s3Options={{ expires: 3600 }} // 1 hour expiration for avatars
        style={{ 
          width: '100%', 
          height: '100%',
          borderRadius: shape === 'circle' ? '50%' : '6px'
        }}
        showLoading={false} // Don't show loading spinner for small avatars
      />
    </AvatarContainer>
  );
}; 